import React from 'react';
import { Building, DollarSign, TrendingUp, Home, Calendar } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const RentVsBuy: React.FC = () => {
  useSEO({
    title: 'Alugar ou Comprar Imóvel em 2026? Análise Financeira Completa | O Teu Dinheiro',
    description: 'Vale mais a pena morar de aluguel e investir a diferença ou financiar a casa própria em 2026? Veja os cálculos e prós e contras.',
    keywords: 'alugar ou comprar imóvel 2026, vale a pena financiar imóvel, aluguel vs compra, custo de oportunidade imóvel',
    url: 'https://www.oteudinheiro.com.br/aluguel-vs-compra'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white py-20">
        <div className="container-custom text-center">
          <Building className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Aluguel vs Compra
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            Qual opção é melhor para você? Analise os prós e contras de cada escolha
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Comparando as duas opções
          </h2>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Aluguel */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <Home className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Aluguel</h3>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Flexibilidade</h4>
                    <p className="text-gray-600 text-sm">Pode mudar de local facilmente</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Sem manutenção</h4>
                    <p className="text-gray-600 text-sm">Proprietário cuida dos problemas</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Menor compromisso</h4>
                    <p className="text-gray-600 text-sm">Não precisa de entrada grande</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-red-600 font-bold">✗</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Sem patrimônio</h4>
                    <p className="text-gray-600 text-sm">Dinheiro não se transforma em ativo</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-red-600 font-bold">✗</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Reajustes</h4>
                    <p className="text-gray-600 text-sm">Aluguel pode aumentar com o tempo</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Compra */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <Building className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Compra</h3>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Patrimônio</h4>
                    <p className="text-gray-600 text-sm">Construa um ativo valioso</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Estabilidade</h4>
                    <p className="text-gray-600 text-sm">Parcela fixa por muitos anos</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Liberdade</h4>
                    <p className="text-gray-600 text-sm">Pode reformar e personalizar</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-red-600 font-bold">✗</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Alto compromisso</h4>
                    <p className="text-gray-600 text-sm">Precisa de entrada e financiamento</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-red-600 font-bold">✗</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Responsabilidades</h4>
                    <p className="text-gray-600 text-sm">Manutenção, IPTU, condomínio</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Simule e compare
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-xl p-8">
              <p className="text-center text-gray-600 mb-8">
                Use nossa calculadora para comparar o custo do aluguel vs compra
                e tomar a melhor decisão para sua situação.
              </p>

              <div className="text-center">
                <a
                  href="/calculadoras"
                  className="btn bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Acessar Calculadora
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Factors */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Fatores para sua decisão
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Situação financeira
              </h3>
              <p className="text-gray-600">
                Tem entrada para compra? Renda estável?
                Considere sua capacidade de pagamento.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Tempo no local
              </h3>
              <p className="text-gray-600">
                Vai ficar por muitos anos? Aluguel é melhor
                para mudanças frequentes.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Objetivos pessoais
              </h3>
              <p className="text-gray-600">
                Quer construir patrimônio? Prioriza estabilidade?
                Alinhamento com seus objetivos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-emerald-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Tome a decisão certa para você!
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Não existe resposta certa para todos. Analise sua situação e escolha o que faz mais sentido.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/calculadoras"
              className="btn bg-white text-emerald-600 hover:bg-gray-100"
            >
              Simular Comparação
            </a>
            <a
              href="/financiamento"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-emerald-600"
            >
              Ver Financiamento
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RentVsBuy;
